// src/components/PremiumPaywall.tsx

import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { close, checkmarkCircle, sparkles, mail } from 'ionicons/icons';
import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { usePremium } from '../hooks/usePremium';
import './PremiumPaywall.css';

interface PremiumPaywallProps {
  isOpen: boolean;
  onDismiss: () => void;
  feature: string; // What feature triggered the paywall
}

export const PremiumPaywall: React.FC<PremiumPaywallProps> = ({
  isOpen,
  onDismiss,
  feature,
}) => {
  const { purchasePackage, isRevenueCatReady } = usePremium();
  const [purchasing, setPurchasing] = useState(false);
  const [presentToast] = useIonToast();

  const handlePurchase = async (packageType: 'monthly' | 'annual') => {
    const isNativePlatform = Capacitor.isNativePlatform();

    // On web, subscriptions are only available via mobile app
    if (!isNativePlatform) {
      await presentToast({
        message: '💳 Subscriptions are available in the mobile app. Contact support@dice-cat.com for assistance.',
        duration: 4000,
        color: 'warning',
        position: 'top',
      });
      return;
    }

    // On native platforms (iOS/Android), always attempt purchase
    // RevenueCat will handle errors and communicate any issues
    setPurchasing(true);

    try {
      const success = await purchasePackage(packageType);

      if (success) {
        await presentToast({
          message: '🎉 Welcome to Dice Cat Plus! Enjoy unlimited features!',
          duration: 3000,
          color: 'success',
          position: 'top',
        });
        onDismiss();
      }
    } catch (error: any) {
      // Show user-friendly error message
      let errorMessage = 'Purchase failed. Please try again.';

      // Handle specific error cases
      if (error.message?.includes('cancelled') || error.message?.includes('canceled')) {
        errorMessage = 'Purchase was cancelled.';
      } else if (error.message?.includes('not available') || error.message?.includes('not configured')) {
        errorMessage = 'Subscriptions are temporarily unavailable. Please try again later or contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      await presentToast({
        message: errorMessage,
        duration: 4000,
        color: 'danger',
        position: 'top',
      });
    } finally {
      setPurchasing(false);
    }
  };

  const handleContactSupport = () => {
    window.open('mailto:support@dice-cat.com?subject=Early Access to Dice Cat Plus', '_system');
  };

  const getFeatureMessage = () => {
    switch (feature) {
      case 'groups':
        return 'Free users can only be in 3 groups at a time.';
      case 'sessions':
        return 'Free users can only create 1 session per month.';
      case 'swipes':
        return "You've reached your daily swipe limit!";
      case 'filters':
        return 'Advanced filters are a Premium feature.';
      case 'rewind':
        return 'Undo accidental swipes with Premium!';
      case 'capture_attempts':
        return "You've used all your capture attempts for this hour!";
      case 'nerdle_archive':
        return "Free users can play 3 archived puzzles!";
      default:
        return 'This is a Premium feature.';
    }
  };

  const getFeatureDescription = () => {
    switch (feature) {
      case 'capture_attempts':
        return 'Upgrade to Dice Cat Plus to get unlimited capture attempts and explore without limits!';
      case 'nerdle_archive':
        return 'Upgrade to Dice Cat Plus to unlock the full Nerdle puzzle archive and play any puzzle anytime!';
      default:
        return 'Upgrade to Dice Cat Plus to unlock this feature and more!';
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss} className="premium-paywall-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>Upgrade to Premium</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="paywall-container">
          {/* Feature Message */}
          <IonCard className="feature-card">
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={sparkles} style={{ marginRight: '8px', color: 'var(--ion-color-warning)' }} />
                {getFeatureMessage()}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <p><strong>{getFeatureDescription()}</strong></p>
            </IonCardContent>
          </IonCard>

          {/* Solo Developer Message */}
          <IonCard style={{ marginTop: '16px', background: 'var(--ion-color-light)' }}>
            <IonCardContent style={{ padding: '16px' }}>
              <p style={{ fontSize: '14px', lineHeight: '1.6', margin: 0, color: 'var(--ion-color-dark)' }}>
                <strong>Hey! 👋</strong> I'm a solo developer building Dice Cat full-time as a freelancer.
                Your support helps me keep the servers running, add new features, and dedicate my days
                to making Dice Cat the best way to find gaming friends. Every subscription truly makes a difference!
              </p>
            </IonCardContent>
          </IonCard>

          {/* Premium Features List */}
          <IonList className="features-list">
            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Unlimited Swipes</h3>
                <p>Find your perfect gaming partner without limits</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Unlimited Groups</h3>
                <p>Join or create as many gaming groups as you want</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Unlimited Sessions</h3>
                <p>Organize gaming sessions anytime</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Unlimited Sanctuary Captures</h3>
                <p>Explore and capture creatures without hourly limits</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Full Nerdle Archive</h3>
                <p>Play any archived puzzle anytime, unlimited access</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Advanced Filters</h3>
                <p>Find exactly who you're looking for</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Rewind Swipes</h3>
                <p>Undo accidental left swipes</p>
              </IonLabel>
            </IonItem>

            <IonItem lines="none">
              <IonIcon icon={checkmarkCircle} slot="start" color="success" />
              <IonLabel>
                <h3>Premium Avatar Items</h3>
                <p>Customize your avatar with exclusive accessories</p>
              </IonLabel>
            </IonItem>
          </IonList>

          {/* Pricing */}
          <IonCard className="pricing-card">
            <IonCardContent>
              <div className="pricing-option" style={{ marginBottom: '16px' }}>
                <h2>Monthly</h2>
                <div className="price">
                  <span className="amount">€6.99</span>
                  <span className="period">/month</span>
                </div>
              </div>

              <div className="pricing-option">
                <h2>Yearly</h2>
                <div className="price">
                  <span className="amount">€59.99</span>
                  <span className="period">/year</span>
                </div>
                <IonNote style={{ color: 'var(--ion-color-success)', fontWeight: 'bold' }}>
                  Save 29% 🎉
                </IonNote>
              </div>
            </IonCardContent>
          </IonCard>

          {/* CTA Buttons */}
          <div className="cta-buttons">
            {isRevenueCatReady ? (
              <>
                <IonButton
                  expand="block"
                  size="large"
                  onClick={() => handlePurchase('annual')}
                  disabled={purchasing}
                  color="success"
                >
                  {purchasing ? <IonSpinner name="crescent" /> : 'Get Yearly - €59.99'}
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={() => handlePurchase('monthly')}
                  disabled={purchasing}
                >
                  {purchasing ? <IonSpinner name="crescent" /> : 'Get Monthly - €6.99'}
                </IonButton>
              </>
            ) : (
              <>
                <IonNote
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    marginTop: '12px',
                    fontSize: '16px',
                    padding: '20px',
                    background: 'var(--ion-color-primary-tint)',
                    borderRadius: '12px',
                    color: 'var(--ion-color-primary-contrast)'
                  }}
                >
                  📱 <strong>Subscriptions are available on the mobile app!</strong>
                  <br /><br />
                  Download the Dice Cat mobile app to subscribe and unlock all premium features.
                </IonNote>
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={handleContactSupport}
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={mail} slot="start" />
                  Questions? Contact Support
                </IonButton>
              </>
            )}

            <IonButton expand="block" fill="clear" onClick={onDismiss}>
              Maybe Later
            </IonButton>
          </div>

          <IonNote className="terms-note">
            <p>Cancel anytime. No tricks, no hassle.</p>
            <p style={{ marginTop: '12px' }}>
              By subscribing, you agree to our{' '}
              <a
                href="https://dice-cat.com/terms.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--ion-color-primary)', textDecoration: 'underline' }}
              >
                Terms of Use
              </a>
              {' '}and{' '}
              <a
                href="https://dice-cat.com/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--ion-color-primary)', textDecoration: 'underline' }}
              >
                Privacy Policy
              </a>
              .
            </p>
            <p style={{ marginTop: '8px', fontStyle: 'italic' }}>
              Thank you for supporting indie development! 💙
            </p>
          </IonNote>
        </div>
      </IonContent>
    </IonModal>
  );
};